import { useMsal } from '@azure/msal-react'
import {
  Button, Checkbox, Group, Modal, MultiSelect,
  Stack, Switch, Text, TextInput, Textarea, Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { CommunityGem, CreateUpdateGemRequest, DayOfWeek, GemHoursRequest, GemPhotoRequest } from '../../../models/CommunityGem'
import { GemTag } from '../../../models/GemTag'
import { adminCreateGem, adminUpdateGem } from '../../../data/communityGems.service'

const DAYS: DayOfWeek[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface Props {
  gem: CommunityGem | null
  tags: GemTag[]
  opened: boolean
  onClose: () => void
  onSaved: (gem: CommunityGem) => void
}

const emptyHours = (): GemHoursRequest[] =>
  DAYS.map(day => ({ dayOfWeek: day, openTime: '09:00', closeTime: '17:00', isClosed: false }))

function GemForm({ gem, tags, opened, onClose, onSaved }: Props) {
  const { instance } = useMsal()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [instagramUrl, setInstagramUrl] = useState('')
  const [facebookUrl, setFacebookUrl] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [photos, setPhotos] = useState<GemPhotoRequest[]>([])
  const [hours, setHours] = useState<GemHoursRequest[]>(emptyHours())
  const [newPhotoUrl, setNewPhotoUrl] = useState('')

  useEffect(() => {
    if (gem) {
      setName(gem.name)
      setDescription(gem.description ?? '')
      setAddress(gem.address ?? '')
      setCity(gem.city ?? '')
      setState(gem.state ?? '')
      setZipCode(gem.zipCode ?? '')
      setWebsiteUrl(gem.websiteUrl ?? '')
      setInstagramUrl(gem.instagramUrl ?? '')
      setFacebookUrl(gem.facebookUrl ?? '')
      setIsPublished(gem.isPublished)
      setSelectedTagIds(gem.tags.map(t => String(t.id)))
      setPhotos(gem.photos.map(p => ({ imageUrl: p.imageUrl, sortOrder: p.sortOrder })))
      setHours(gem.hours.length > 0
        ? gem.hours.map(h => ({ dayOfWeek: h.dayOfWeek, openTime: h.openTime, closeTime: h.closeTime, isClosed: h.isClosed }))
        : emptyHours()
      )
    } else {
      setName(''); setDescription(''); setAddress(''); setCity(''); setState(''); setZipCode('')
      setWebsiteUrl(''); setInstagramUrl(''); setFacebookUrl(''); setIsPublished(false)
      setSelectedTagIds([]); setPhotos([]); setHours(emptyHours())
    }
  }, [gem, opened])

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const request: CreateUpdateGemRequest = {
        name: name.trim(),
        description: description || null,
        address: address || null,
        city: city || null,
        state: state || null,
        zipCode: zipCode || null,
        latitude: null,
        longitude: null,
        websiteUrl: websiteUrl || null,
        instagramUrl: instagramUrl || null,
        facebookUrl: facebookUrl || null,
        isPublished,
        locationId: null,
        tagIds: selectedTagIds.map(Number),
        photos,
        hours,
      }
      const saved = gem
        ? await adminUpdateGem(instance, gem.id, request)
        : await adminCreateGem(instance, request)
      onSaved(saved)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const tagOptions = tags.map(t => ({ value: String(t.id), label: `${t.name} (${t.tagType})` }))

  return (
    <Modal opened={opened} onClose={onClose} title={gem ? 'Edit Gem' : 'Add Gem'} size='xl'>
      <Stack>
        <TextInput label='Name' required value={name} onChange={e => setName(e.target.value)} />
        <Textarea label='Description' value={description} onChange={e => setDescription(e.target.value)} rows={3} />
        <TextInput label='Address' value={address} onChange={e => setAddress(e.target.value)} />
        <Group grow>
          <TextInput label='City' value={city} onChange={e => setCity(e.target.value)} />
          <TextInput label='State' value={state} onChange={e => setState(e.target.value)} />
          <TextInput label='Zip Code' value={zipCode} onChange={e => setZipCode(e.target.value)} />
        </Group>
        <TextInput label='Website URL' value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} />
        <TextInput label='Instagram URL' value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} />
        <TextInput label='Facebook URL' value={facebookUrl} onChange={e => setFacebookUrl(e.target.value)} />
        <MultiSelect label='Tags' data={tagOptions} value={selectedTagIds} onChange={setSelectedTagIds} searchable />
        <Switch label='Published (visible on site)' checked={isPublished} onChange={e => setIsPublished(e.currentTarget.checked)} />

        <Title order={5}>Photos</Title>
        {photos.map((p, i) => (
          <Group key={i}>
            <Text size='sm' style={{ flex: 1 }}>{p.imageUrl}</Text>
            <Button size='xs' color='red' variant='subtle' onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}>Remove</Button>
          </Group>
        ))}
        <Group>
          <TextInput
            placeholder='Paste Azure Blob URL'
            value={newPhotoUrl}
            onChange={e => setNewPhotoUrl(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button size='xs' onClick={() => {
            if (newPhotoUrl) {
              setPhotos(prev => [...prev, { imageUrl: newPhotoUrl, sortOrder: prev.length }])
              setNewPhotoUrl('')
            }
          }}>
            Add Photo
          </Button>
        </Group>

        <Title order={5}>Business Hours</Title>
        {hours.map((h, i) => (
          <Group key={h.dayOfWeek} align='center'>
            <Text w={90} size='sm'>{h.dayOfWeek}</Text>
            <Checkbox
              label='Closed'
              checked={h.isClosed}
              onChange={e => setHours(prev => prev.map((d, idx) => idx === i ? { ...d, isClosed: e.currentTarget.checked } : d))}
            />
            {!h.isClosed && (
              <>
                <TextInput
                  type='time'
                  value={h.openTime ?? ''}
                  onChange={e => setHours(prev => prev.map((d, idx) => idx === i ? { ...d, openTime: e.target.value } : d))}
                />
                <Text size='sm'>–</Text>
                <TextInput
                  type='time'
                  value={h.closeTime ?? ''}
                  onChange={e => setHours(prev => prev.map((d, idx) => idx === i ? { ...d, closeTime: e.target.value } : d))}
                />
              </>
            )}
          </Group>
        ))}

        <Group justify='flex-end' mt='md'>
          <Button variant='subtle' onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save Gem</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default GemForm
